import { CheckmarkFilled, RadioButton } from '@carbon/icons-react';
import {
  Box,
  Container,
  Step,
  StepLabel,
  Stepper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import { Assessment, GitHubProvisioning } from '../gihub-coding-page';
import {
  GitHubCodingStep,
  GitHubCodingStepStatus,
  getCurrentStep,
  getStepStatus,
} from '../utils';

interface StepData {
  step: GitHubCodingStep;
  label: string;
  description: string;
  status: GitHubCodingStepStatus;
}

const getSteps = (
  assessment: Assessment,
  provisioning?: GitHubProvisioning | null,
): StepData[] => {
  const currentStepIndex = getCurrentStep(assessment, provisioning);

  const steps = [
    {
      step: GitHubCodingStep.AssessmentStarted,
      label: 'Assessment started',
      description: 'You have been invited to the assessment repository',
    },
    {
      step: GitHubCodingStep.InvitationAccepted,
      label: 'Invitation accepted',
      description: 'You have joined the repository and can start coding',
    },
    {
      step: GitHubCodingStep.CodeSubmitted,
      label: 'Code submitted',
      description: 'You have pushed your solution to the repository',
    },
    {
      step: GitHubCodingStep.AssessmentCompleted,
      label: 'Assessment submitted',
      description: 'You have submitted your assessment for review',
    },
  ];

  return steps.map((step) => ({
    ...step,
    status: getStepStatus(step.step, currentStepIndex),
  }));
};

const getStepIcon = (status: GitHubCodingStepStatus, theme: Theme) => {
  switch (status) {
    case GitHubCodingStepStatus.Completed:
      return (
        <CheckmarkFilled
          size={24}
          style={{ color: theme.palette.success.main }}
        />
      );
    default:
      return (
        <RadioButton
          size={24}
          style={{ color: theme.palette.action.disabled }}
        />
      );
  }
};

export const GithubCodingHeader = ({
  assessment,
  provisioning,
}: {
  assessment: Assessment;
  provisioning?: GitHubProvisioning | null;
}) => {
  const theme = useTheme();
  const steps = getSteps(assessment, provisioning);
  const activeStep = getCurrentStep(assessment, provisioning);

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 4,
      }}
    >
      <Container>
        <Typography variant='h6' gutterBottom>
          {assessment.template.name}
        </Typography>
        <Typography variant='body1' color='text.secondary' gutterBottom>
          {assessment.template.description}
        </Typography>

        <Box sx={{ mt: 5 }}>
          <Stepper
            activeStep={activeStep}
            orientation='horizontal'
            alternativeLabel={true}
          >
            {steps.map((step) => (
              <Step
                key={step.label}
                completed={step.status === GitHubCodingStepStatus.Completed}
              >
                <StepLabel icon={getStepIcon(step.status, theme)}>
                  <Typography variant='subtitle1'>{step.label}</Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', maxWidth: 180, margin: '0 auto' }}
                  >
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Container>
    </Box>
  );
};
